import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { BlogPost, BlogSection } from './blogTypes';
import { pitchSectionIndex } from './blogSectionUtils';

/** Shared long-form renderer for blog posts and the merged competitor comparison pages. */
export function SectionBlock({ section, index }: { section: BlogSection; index: number }) {
  switch (section.type) {
    case 'h2':
      return (
        <h2
          id={`blog-heading-${index}`}
          className="text-2xl font-bold text-gray-900 mt-12 mb-4 scroll-mt-28"
        >
          {section.text}
        </h2>
      );
    case 'h3':
      return (
        <h3
          id={`blog-heading-${index}`}
          className="text-xl font-semibold text-gray-900 mt-10 mb-3 scroll-mt-28"
        >
          {section.text}
        </h3>
      );
    case 'p':
      return (
        <p key={index} className="text-base text-gray-700 leading-[1.75] mb-5">
          {section.text}
        </p>
      );
    case 'ul':
      return (
        <ul key={index} className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
          {section.items.map((item, i) => (
            <li key={i} className="leading-relaxed">
              {item}
            </li>
          ))}
        </ul>
      );
    case 'ol':
      return (
        <ol key={index} className="list-decimal pl-6 space-y-2 text-gray-700 mb-6">
          {section.items.map((item, i) => (
            <li key={i} className="leading-relaxed">
              {item}
            </li>
          ))}
        </ol>
      );
    case 'quote':
      return (
        <blockquote
          key={index}
          className="border-l-4 border-[#1769ff] pl-5 py-2 my-6 bg-blue-50/50 rounded-r-xl text-gray-800 italic"
        >
          <p className="mb-1">"{section.text}"</p>
          {section.attribution && (
            <cite className="text-sm not-italic text-gray-500">— {section.attribution}</cite>
          )}
        </blockquote>
      );
    case 'infographic':
      return (
        <figure key={index} className="my-8">
          <div className="rounded-2xl overflow-hidden border border-gray-200 bg-gray-50 shadow-sm">
            <img
              src={section.image}
              alt={section.alt}
              className="w-full h-auto object-contain max-h-[480px]"
            />
          </div>
          {section.caption && (
            <figcaption className="text-sm text-gray-500 mt-2 text-center">{section.caption}</figcaption>
          )}
        </figure>
      );
    case 'relatedReading':
      return (
        <div key={index} className="my-8 rounded-2xl border border-gray-200 bg-gray-50/80 p-5 lg:p-6">
          <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3">{section.heading}</h2>
          <ul className="space-y-2">
            {section.links
              .filter((link, i, all) => all.findIndex((l) => l.to === link.to) === i)
              .map((link) => (
              <li key={link.to}>
                {/^https?:\/\//.test(link.to) ? (
                  <a
                    href={link.to}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="text-[#1769ff] hover:text-blue-700 font-medium text-sm inline-flex items-center gap-1"
                  >
                    {link.label}
                    <i className="ri-external-link-line text-xs" />
                  </a>
                ) : (
                  <Link
                    to={link.to}
                    className="text-[#1769ff] hover:text-blue-700 font-medium text-sm inline-flex items-center gap-1"
                  >
                    {link.label}
                    <i className="ri-arrow-right-line text-xs" />
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>
      );
    case 'ctaRow':
      return (
        <div key={index} className="my-8 flex flex-col sm:flex-row flex-wrap gap-3">
          {section.items.map((item) =>
            item.external ? (
              <a
                key={item.href + item.label}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex justify-center items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold bg-[#1769ff] text-white hover:bg-blue-700 transition-colors shadow-sm"
              >
                {item.label}
                <i className="ri-external-link-line text-sm opacity-90" />
              </a>
            ) : (
              <Link
                key={item.href + item.label}
                to={item.href}
                className="inline-flex justify-center items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold border-2 border-[#1769ff] text-[#1769ff] hover:bg-blue-50 transition-colors"
              >
                {item.label}
                <i className="ri-arrow-right-line text-sm" />
              </Link>
            )
          )}
        </div>
      );
    case 'table':
      return (
        <figure key={index} className="my-8">
          {/* Phones: one card per row, each cell labelled with its column heading */}
          <ul className="m-0 list-none space-y-3 p-0 md:hidden">
            {section.rows.map((row, r) => (
              <li key={r} className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                <p className="border-b border-gray-100 bg-gray-50 px-4 py-2.5 text-sm font-semibold text-gray-900">{row[0]}</p>
                <dl className="divide-y divide-gray-100">
                  {row.slice(1).map((cell, c) => (
                    <div key={c} className="px-4 py-2.5">
                      <dt className="text-[11px] font-bold uppercase tracking-wider text-gray-400">{section.headers[c + 1]}</dt>
                      <dd className="mt-0.5 text-sm leading-relaxed text-gray-700">{cell}</dd>
                    </div>
                  ))}
                </dl>
              </li>
            ))}
          </ul>
          <div className="hidden overflow-x-auto rounded-2xl border border-gray-200 shadow-sm md:block">
            <table className="w-full text-sm text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  {section.headers.map((h, i) => (
                    <th key={i} className="px-4 py-3 font-semibold text-gray-900 whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {section.rows.map((row, r) => (
                  <tr key={r} className="border-b border-gray-100 last:border-0 even:bg-gray-50/40">
                    {row.map((cell, c) => (
                      <td
                        key={c}
                        className={`px-4 py-3 align-top text-gray-700 ${c === 0 ? 'font-medium text-gray-900' : ''}`}
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {section.caption && (
            <figcaption className="text-sm text-gray-500 mt-2 text-center">{section.caption}</figcaption>
          )}
        </figure>
      );
    case 'callout': {
      const variant = section.variant ?? 'info';
      const icon =
        variant === 'stat' ? 'ri-bar-chart-2-line' : variant === 'tip' ? 'ri-lightbulb-flash-line' : 'ri-information-line';
      return (
        <div
          key={index}
          className="my-8 flex gap-4 rounded-2xl border border-blue-100 bg-blue-50/60 p-5 lg:p-6"
        >
          <i className={`${icon} text-2xl text-[#1769ff] shrink-0`} aria-hidden />
          <div className="min-w-0">
            {section.value && (
              <div className="text-2xl lg:text-3xl font-bold text-[#1769ff] leading-tight mb-1">{section.value}</div>
            )}
            {section.title && <p className="font-semibold text-gray-900 mb-1">{section.title}</p>}
            <p className="text-gray-700 leading-relaxed">{section.text}</p>
          </div>
        </div>
      );
    }
    default:
      return null;
  }
}


export function GeofleetPitch({ proofPoint }: { proofPoint?: BlogPost['proofPoint'] }) {
  const { t } = useTranslation();
  return (
    <aside className="not-prose my-10 rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 to-white p-6 lg:p-7 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-wider text-[#1769ff] mb-2">{t('blog.post.pitch.eyebrow')}</p>
      <p className="text-xl font-bold text-gray-900 mb-2">{t('blog.post.pitch.title')}</p>
      <p className="text-gray-700 leading-relaxed mb-5">{t('blog.post.pitch.text')}</p>
      {proofPoint && (
        <div className="flex items-start gap-4 rounded-xl bg-white border border-blue-100 px-4 py-3 mb-5">
          <span className="text-2xl font-bold text-[#1769ff] leading-tight shrink-0">{proofPoint.value}</span>
          <p className="text-sm text-gray-700 leading-relaxed">
            {proofPoint.text}{' '}
            <Link to={proofPoint.to} className="font-medium text-[#1769ff] hover:text-blue-700 whitespace-nowrap">
              {t('blog.post.pitch.caseStudy')} <i className="ri-arrow-right-line" aria-hidden />
            </Link>
          </p>
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-3">
        <a
          href="https://app.geofleet.ai/signup"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex justify-center items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold bg-[#1769ff] text-white hover:bg-blue-700 transition-colors shadow-sm"
        >
          {t('blog.post.pitch.startFree')}
          <i className="ri-arrow-right-line text-sm" aria-hidden />
        </a>
        <a
          href="https://meetings.hubspot.com/aditya145"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex justify-center items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold border-2 border-[#1769ff] text-[#1769ff] hover:bg-blue-50 transition-colors"
        >
          {t('blog.post.pitch.bookDemo')}
        </a>
      </div>
    </aside>
  );
}


/** Article body with an optional mid-article Geofleet box. */
export function ArticleSections({
  sections,
  proofPoint,
  withPitch,
}: {
  sections: BlogSection[];
  proofPoint?: BlogPost['proofPoint'];
  withPitch: boolean;
}) {
  const pitchIndex = withPitch ? pitchSectionIndex(sections) : -1;
  return (
    <>
      {sections.map((section, i) => (
        <Fragment key={i}>
          {i === pitchIndex && <GeofleetPitch proofPoint={proofPoint} />}
          <SectionBlock section={section} index={i} />
        </Fragment>
      ))}
    </>
  );
}
